/*
 * VoxPont product catalog — the canonical list of selectable VoxPont products.
 *
 * Single source of truth is the marketing data (slug -> route + copy), so the
 * onboarding picker and the in-app product switcher never drift from the public
 * marketing site. We only add a per-slug icon here for the picker UI.
 */

import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import CrisisAlertOutlinedIcon from "@mui/icons-material/CrisisAlertOutlined";
import LocalPharmacyOutlinedIcon from "@mui/icons-material/LocalPharmacyOutlined";
import TabletMacOutlinedIcon from "@mui/icons-material/TabletMacOutlined";
import Diversity3OutlinedIcon from "@mui/icons-material/Diversity3Outlined";
import SignalCellularAltOutlinedIcon from "@mui/icons-material/SignalCellularAltOutlined";
import {
  HUB,
  PRODUCT_ROUTE_BY_SLUG,
} from "pages/voxpontmarketing/voxpontMarketingData";

const ICON_BY_SLUG = {
  medical: MedicalServicesOutlinedIcon,
  education: SchoolOutlinedIcon,
  crisis: CrisisAlertOutlinedIcon,
  pharmacy: LocalPharmacyOutlinedIcon,
  kiosk: TabletMacOutlinedIcon,
  social: Diversity3OutlinedIcon,
  lowbandwidth: SignalCellularAltOutlinedIcon,
};

/**
 * The selectable products, in marketing-hub order. Each entry:
 * { slug, route, name, domain, description, accent, Icon }
 * A product is only included when it has both a live route and an icon, so an
 * un-routed marketing teaser can never appear as a pickable dashboard.
 */
export const VOXPONT_PRODUCTS = HUB.products
  .filter((p) => PRODUCT_ROUTE_BY_SLUG[p.slug] && ICON_BY_SLUG[p.slug])
  .map((p) => ({
    slug: p.slug,
    route: PRODUCT_ROUTE_BY_SLUG[p.slug],
    name: p.name,
    domain: p.domain,
    description: p.desc,
    accent: p.accent,
    Icon: ICON_BY_SLUG[p.slug],
  }));

const VOXPONT_PRODUCT_BY_SLUG = VOXPONT_PRODUCTS.reduce((acc, p) => {
  acc[p.slug] = p;
  return acc;
}, {});

const VOXPONT_PRODUCT_BY_ROUTE = VOXPONT_PRODUCTS.reduce((acc, p) => {
  acc[p.route] = p;
  return acc;
}, {});

/** Resolve a product by its stored slug (e.g. "medical"). Returns null if unknown. */
export function getVoxPontProductBySlug(slug) {
  if (!slug) return null;
  return VOXPONT_PRODUCT_BY_SLUG[slug] || null;
}

/** Resolve a product by its app route (e.g. "/voxpont4"). Returns null if unknown. */
export function getVoxPontProductByRoute(route) {
  if (!route) return null;
  return VOXPONT_PRODUCT_BY_ROUTE[route] || null;
}
